import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';
import ROUTES from '@/routes/routes';
import { centsToCurrency, currencyToCents } from '@/util/number';
import { CalculateCost } from '@/util/payments';

import { JobDraftV2 } from '../../context';
import { createOrderV2Client } from '../../api/client';
import {
  BillingAccountV2,
  PaymentMethodsResponse,
  SkillListItem,
  SurgeRateV2,
} from '../../api/types';
import ShiftPopup, {
  breakLabelFromMinutes,
  centeredPopupPosition,
  minutesFromBreakLabel,
  time12To24,
  time24To12,
} from '../ScheduleStep/ShiftPopup';

import {
  AddSkillsButton,
  Card,
  CardHeader,
  CardTitle,
  Chevron,
  Collapsible,
  IconButton,
  JobHeaderRow,
  JobIcon,
  JobIconImg,
  JobMeta,
  JobMetaText,
  JobName,
  KeyValueRow,
  KvLabel,
  KvValue,
  LeftColumn,
  OrderHeading,
  PageShell,
  PaymentBody,
  PaymentLabel,
  PaymentMuted,
  RateInputDollar,
  RateInput,
  RateInputShell,
  RatesSection,
  RightColumn,
  ScheduleAction,
  ScheduleActions,
  ScheduleDateCell,
  ScheduleHeaderCell,
  ScheduleHeaderRow,
  ScheduleHeaderTime,
  ScheduleHeaderTimeLabel,
  ScheduleHeaderTz,
  ScheduleLabel,
  ScheduleRow,
  ScheduleRowsWrap,
  ScheduleTable,
  ScheduleTimeCell,
  ScheduleTimeMain,
  ScheduleTimeSub,
  ScheduleToggleInner,
  ScheduleToggleRow,
  ScheduleWorkersCell,
  TwoColumn,
} from './styles';

type ReviewStepProps = {
  orderType: 'GIG' | 'LTA' | null;
  jobs: JobDraftV2[];
  skills?: SkillListItem[];
  paymentMethods?: PaymentMethodsResponse | null;
  paymentMethodsLoading?: boolean;
  paymentMethodsError?: string | null;
  billingAccountName?: string | null;
  // Legacy v1 heading is `Order for ${billing.customer.name}`. When the
  // wizard can resolve the parent customer (typical case), the review
  // header prefers `customerName`; otherwise we fall back to the billing
  // account label to keep the heading non-empty.
  customerName?: string | null;
  billingAccount?: BillingAccountV2 | null;
  onUpdateJobs?: (jobs: JobDraftV2[]) => void;
  onAddSkill?: () => void;
  surgeRates?: SurgeRateV2[];
  // Aggregated validity across the editable pay-rate inputs. False when
  // any tenant-admin editable job has a pay rate below its rate-range
  // minimum, so the parent can disable the Submit button (v1 parity:
  // CustomErrorsForm on the summary blocked submit when below-min).
  onRatesValidityChange?: (valid: boolean) => void;
  // Reports whether the billing account has a `current_payment_method`.
  // False blocks Submit at the parent (v1 parity: legacy Billing form
  // disabled "Start order" when the account had no current_payment_method).
  onPaymentMethodValidityChange?: (valid: boolean) => void;
};

// Total hours rolls up *per-shift quantity*, not the job-level quantity.
// When shifts under the same job have different worker counts (e.g.
// 8 hrs × 1 worker + 8 hrs × 3 workers = 32 hrs, NOT 8+8=16 hrs ×
// `job.quantity`), the older "sum durations, multiply by job qty"
// approach scaled every shift by the same number and over-/under-
// counted whenever shifts diverged. Falling back to `job.quantity` for
// shifts missing their own quantity keeps legacy single-quantity orders
// behaving the same way.
const totalShiftHours = (job: JobDraftV2) =>
  (job.shifts || []).reduce((sum, shift) => {
    const start = new Date(shift.start_at).getTime();
    const end = new Date(shift.end_at).getTime();
    let diffMs = end - start;
    if (diffMs <= 0) diffMs += 24 * 3_600_000;
    const hours = diffMs / 3_600_000 - (shift.break_minutes || 0) / 60;
    const qty = shift.quantity ?? job.quantity ?? 1;
    return sum + Math.max(hours, 0) * qty;
  }, 0);

const formatDate = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateRange = (job: JobDraftV2) => {
  const shifts = job.shifts || [];
  if (shifts.length === 0) return '—';
  const sorted = [...shifts].sort((a, b) =>
    a.start_at.localeCompare(b.start_at),
  );
  const first = formatDate(sorted[0].start_at);
  const last = formatDate(sorted[sorted.length - 1].start_at);
  return first === last ? first : `${first} – ${last}`;
};

const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const breakLabel = (mins?: number) =>
  `${mins || 0} min${mins === 1 ? '' : 's'} break`;
const toDateLabel = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DAY_ABBR[d.getDay()]}, ${MONTH_ABBR[d.getMonth()]} ${ordinal(d.getDate())}, ${d.getFullYear()}`;
};

const shiftHours = (startIso: string, endIso: string, breakMins?: number) => {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  let diffMs = end - start;
  if (diffMs <= 0) diffMs += 24 * 3_600_000;
  const hours = diffMs / 3_600_000 - (breakMins || 0) / 60;
  return Math.max(hours, 0);
};

const formatHoursLabel = (hours: number) => {
  if (hours <= 0) return '';
  const rounded = Math.round(hours * 100) / 100;
  return `(${rounded.toFixed(2)} hrs)`;
};
const centsToDollars = (value?: number) => (value || 0) / 100;

const formatCard = (payment: PaymentMethodsResponse | null | undefined) => {
  if (!payment) return null;
  if (payment.primary_card_label) return payment.primary_card_label;
  const card = payment.cards?.[0];
  if (!card) return null;
  const brand = (card.brand || '').replace(/^\w/, (c) => c.toUpperCase());
  return `${brand} **** ${card.last4 || '••••'}`;
};

const ChevronIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="#44A735"
    strokeWidth={2}
    width={16}
    height={16}
  >
    <polyline points="5 8 10 13 15 8" />
  </svg>
);

const PlusIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="#44A735"
    strokeWidth={2}
    width={18}
    height={18}
  >
    <line x1="10" y1="4" x2="10" y2="16" />
    <line x1="4" y1="10" x2="16" y2="10" />
  </svg>
);

const PencilIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    stroke="#979797"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 3.3a1 1 0 0 1 1.4 1.4l-9.9 9.9-2.8.7.7-2.8 9.9-9.9z" />
    <path d="M13 5l2 2" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    stroke="#979797"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="5 7 15 7" />
    <path d="M8 7V5h4v2" />
    <rect x={6} y={7} width={8} height={9} rx={1} />
  </svg>
);

type JobCardProps = {
  job: JobDraftV2;
  index: number;
  skills?: SkillListItem[];
  defaultOpen?: boolean;
  isMarkup?: boolean;
  markupPercent?: number;
  billingAccountId?: number | null;
  onUpdateJob?: (next: JobDraftV2) => void;
  // Per-job validity for the editable pay-rate input on review.
  // Reports `true` when the rate is not editable here (read-only branch)
  // or sits at/above the fetched market minimum; reports `false` when an
  // editable input has been driven below minimum.
  onValidityChange?: (index: number, valid: boolean) => void;
};

// Mirrors legacy CreateGigOrder PayRateStep / SummaryTableRevamped color rule:
// red below market minimum, orange below market max, green at/above max.
const rateColor = (payCents: number, minPay: number, maxPay: number) => {
  if (payCents < minPay) return '#DC1515';
  if (payCents < maxPay) return '#EC7F00';
  return '#45A735';
};

type EditTarget = {
  shiftIdx: number;
  dateKey: string;
  startTime: string;
  endTime: string;
  breakTime: string;
  workers: number;
} | null;

const JobCard = ({
  job,
  index,
  skills,
  defaultOpen,
  isMarkup,
  markupPercent,
  billingAccountId,
  onUpdateJob,
  onValidityChange,
}: JobCardProps) => {
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  // Legacy v1 review surface only exposes editable rate inputs on the
  // markup-revamped flow — the static/non-markup `JobRates` component
  // renders Base Pay rate / Base Bill rate as read-only text. Mirror
  // that here so tenant admins on a non-markup account see static rate
  // values on the review page rather than editable inputs. The bill
  // rate row is only shown for non-markup (see `!isMarkup` guard below)
  // so it is always read-only on review.
  const payRateEditable = !!onUpdateJob && !isClientAdmin && isMarkup;
  const [open, setOpen] = useState<boolean>(!!defaultOpen);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const skill = skills?.find((s) => s.id === job.skill_id) || null;
  const skillName = skill?.display_name_en || skill?.name || `Job ${index + 1}`;
  const shiftCount = (job.shifts || []).length;
  const hours = totalShiftHours(job);
  const [payInput, setPayInput] = useState<string>(() =>
    job.pay_rate ? centsToCurrency(job.pay_rate) : '',
  );

  useEffect(() => {
    setPayInput(job.pay_rate ? centsToCurrency(job.pay_rate) : '');
  }, [job.pay_rate]);

  // Markup billing accounts get the legacy red/yellow/green coloring on the
  // pay rate input, driven by the per-skill/address rate range fetched from
  // the v2 admin REST endpoint (replaces the legacy GraphQL rateRange query).
  const [rateRange, setRateRange] = useState<{
    minimum: number;
    maximum: number;
  } | null>(null);
  useEffect(() => {
    if (!isMarkup || !billingAccountId || !job.skill_id || !job.address_id) {
      setRateRange(null);
      return;
    }
    let cancelled = false;
    createOrderV2Client
      .getRateRange(billingAccountId, job.skill_id, job.address_id)
      .then((range) => {
        if (!cancelled && range) setRateRange(range);
      })
      .catch(() => {
        if (!cancelled) setRateRange(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isMarkup, billingAccountId, job.skill_id, job.address_id]);

  const minPay = rateRange?.minimum ?? 0;
  const maxPay = rateRange?.maximum ?? 0;
  const payValueCents = Math.round(
    (Number(payInput.replace(/[$,]/g, '')) || 0) * 100,
  );
  const payColor = isMarkup
    ? rateColor(payValueCents, minPay, maxPay)
    : undefined;

  // Block submit when this job's editable pay rate sits below the
  // fetched market minimum. We compare against the committed `pay_rate`
  // rather than the live input so a mid-edit doesn't toggle the parent's
  // Submit button on every keystroke — the input's onBlur is what
  // ultimately commits + flips this flag.
  const payBelowMin =
    payRateEditable && minPay > 0 && (job.pay_rate || 0) < minPay;
  useEffect(() => {
    if (!onValidityChange) return;
    onValidityChange(index, !payBelowMin);
  }, [onValidityChange, index, payBelowMin]);

  const commitPayRate = (raw: string) => {
    if (!onUpdateJob) return;
    const trimmed = raw.trim();
    const cents = trimmed ? Math.round(currencyToCents(trimmed)) : 0;
    if (Number.isNaN(cents)) return;
    // Markup accounts derive cost from pay + markup_percent (legacy
    // CalculateCost). Without this, the right-rail "Total estimate" would
    // never refresh when the user edits the pay rate. Bill rate is never
    // editable on the review page (see `JobRates` in legacy v1 for the
    // static read-only parity).
    const cost = CalculateCost(trimmed || '0', markupPercent ?? 0);
    onUpdateJob({ ...job, pay_rate: cents, cost_rate: cost });
  };

  const sortedShifts = [...(job.shifts || [])].sort((a, b) =>
    a.start_at.localeCompare(b.start_at),
  );

  const handleDeleteShift = (idx: number) => {
    if (!onUpdateJob) return;
    const target = sortedShifts[idx];
    if (!target) return;
    const nextShifts = (job.shifts || []).filter(
      (shift) =>
        !(shift.start_at === target.start_at && shift.end_at === target.end_at),
    );
    onUpdateJob({ ...job, shifts: nextShifts });
  };

  const handleOpenEdit = (idx: number) => {
    const shift = sortedShifts[idx];
    if (!shift) return;
    setEditTarget({
      shiftIdx: idx,
      dateKey: shift.start_at.slice(0, 10),
      startTime: time24To12(shift.start_at),
      endTime: time24To12(shift.end_at),
      breakTime: breakLabelFromMinutes(shift.break_minutes ?? 15),
      workers: shift.quantity || 1,
    });
  };

  const handleSaveEdit = (values: {
    startTime: string;
    endTime: string;
    breakTime: string;
    workers: number;
  }) => {
    if (!editTarget || !onUpdateJob) return;
    const target = sortedShifts[editTarget.shiftIdx];
    if (!target) return;
    const dateKey = editTarget.dateKey;
    const nextShifts = (job.shifts || []).map((shift) => {
      if (shift.start_at !== target.start_at || shift.end_at !== target.end_at)
        return shift;
      return {
        ...shift,
        start_at: `${dateKey}T${time12To24(values.startTime)}`,
        end_at: `${dateKey}T${time12To24(values.endTime)}`,
        break_minutes: minutesFromBreakLabel(values.breakTime),
        quantity: values.workers,
      };
    });
    onUpdateJob({ ...job, shifts: nextShifts });
    setEditTarget(null);
  };

  return (
    <Card>
      <JobHeaderRow>
        <JobIcon>
          {skill?.image_url ? (
            <JobIconImg src={skill.image_url} alt="" />
          ) : (
            skillName.slice(0, 1).toUpperCase()
          )}
        </JobIcon>
        <JobMeta>
          <JobName>{skillName}</JobName>
          <JobMetaText>{formatDateRange(job)}</JobMetaText>
          <JobMetaText muted>
            {shiftCount} shift{shiftCount === 1 ? '' : 's'} ·{' '}
            {job.quantity || 1} worker
            {job.quantity === 1 ? '' : 's'} · {hours.toFixed(2)} hrs
          </JobMetaText>
        </JobMeta>
      </JobHeaderRow>

      <ScheduleToggleRow>
        <ScheduleToggleInner type="button" onClick={() => setOpen((o) => !o)}>
          <ScheduleLabel>Schedule</ScheduleLabel>
          <ScheduleAction>
            <span>{open ? 'Hide Schedule' : 'View Schedule'}</span>
            <Chevron open={open}>
              <ChevronIcon />
            </Chevron>
          </ScheduleAction>
        </ScheduleToggleInner>
      </ScheduleToggleRow>

      <Collapsible open={open}>
        <ScheduleTable>
          {sortedShifts.length === 0 ? (
            <ScheduleRowsWrap>
              <ScheduleRow>
                <span>No shifts scheduled</span>
              </ScheduleRow>
            </ScheduleRowsWrap>
          ) : (
            <ScheduleRowsWrap>
              <ScheduleHeaderRow>
                <ScheduleHeaderCell col="date">Date</ScheduleHeaderCell>
                <ScheduleHeaderTime>
                  <ScheduleHeaderTimeLabel>Time</ScheduleHeaderTimeLabel>
                  <ScheduleHeaderTz>EDT</ScheduleHeaderTz>
                </ScheduleHeaderTime>
                <ScheduleHeaderCell col="workers">Workers</ScheduleHeaderCell>
                <ScheduleHeaderCell col="actions">Actions</ScheduleHeaderCell>
              </ScheduleHeaderRow>
              {sortedShifts.map((shift, i) => {
                const qty = shift.quantity || 1;
                const hrs = shiftHours(
                  shift.start_at,
                  shift.end_at,
                  shift.break_minutes,
                );
                const hrsLabel = formatHoursLabel(hrs);
                return (
                  <ScheduleRow key={i}>
                    <ScheduleDateCell>
                      {toDateLabel(shift.start_at)}
                    </ScheduleDateCell>
                    <ScheduleTimeCell>
                      <ScheduleTimeMain>
                        {formatTime(shift.start_at)} -{' '}
                        {formatTime(shift.end_at)}
                      </ScheduleTimeMain>
                      <ScheduleTimeSub>
                        {breakLabel(shift.break_minutes)}
                      </ScheduleTimeSub>
                      {hrsLabel ? (
                        <ScheduleTimeSub>{hrsLabel}</ScheduleTimeSub>
                      ) : null}
                    </ScheduleTimeCell>
                    <ScheduleWorkersCell>
                      <span>
                        {qty} worker{qty === 1 ? '' : 's'}
                      </span>
                      {/*
                        V2 stores invite_workers / hire_workers flat on the
                        job draft. The Staffing screen scopes invites to the
                        per-shift selection, but at submission time the
                        invitations apply to every shift on the job — same
                        behaviour as legacy `buildInvitePayload.ts` which
                        emits an InvitationInput per schedule. So we show
                        the job-wide counts on each shift row, falling back
                        to a dash when nothing is staffed yet.
                      */}
                      {(job.invite_workers?.length ?? 0) > 0 ||
                      (job.hire_workers?.length ?? 0) > 0 ? (
                        <span
                          style={{ color: '#5B5B5B', fontSize: 13 }}
                          aria-label="Staffing summary"
                        >
                          {job.hire_workers?.length
                            ? `${job.hire_workers.length} hired`
                            : null}
                          {job.hire_workers?.length &&
                          job.invite_workers?.length
                            ? ' · '
                            : null}
                          {job.invite_workers?.length
                            ? `${job.invite_workers.length} invited`
                            : null}
                        </span>
                      ) : (
                        <span style={{ color: '#979797' }}>-</span>
                      )}
                    </ScheduleWorkersCell>
                    <ScheduleActions>
                      <IconButton
                        type="button"
                        aria-label="Edit shift"
                        onClick={() => handleOpenEdit(i)}
                      >
                        <PencilIcon />
                      </IconButton>
                      <IconButton
                        type="button"
                        aria-label="Delete shift"
                        onClick={() => handleDeleteShift(i)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </ScheduleActions>
                  </ScheduleRow>
                );
              })}
            </ScheduleRowsWrap>
          )}
        </ScheduleTable>

        <RatesSection>
          <CardHeader>
            <CardTitle>Job rates</CardTitle>
          </CardHeader>
          {!isClientAdmin ? (
            <KeyValueRow>
              <KvLabel>{isMarkup ? 'Pay Rate' : 'Base pay rate'}</KvLabel>
              {payRateEditable ? (
                <RateInputShell>
                  <RateInputDollar>$</RateInputDollar>
                  <RateInput
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={payInput}
                    style={
                      payColor
                        ? { color: payColor, fontWeight: 700 }
                        : undefined
                    }
                    onChange={(event) => setPayInput(event.target.value)}
                    onBlur={() => commitPayRate(payInput)}
                  />
                </RateInputShell>
              ) : (
                <KvValue style={payColor ? { color: payColor } : undefined}>
                  {formatCurrency(centsToDollars(job.pay_rate))}
                </KvValue>
              )}
            </KeyValueRow>
          ) : null}
          {payBelowMin ? (
            <div
              role="alert"
              style={{
                marginTop: 4,
                padding: '8px 12px',
                borderRadius: 6,
                background: '#FDECEC',
                border: '1px solid #F5B7B7',
                color: '#A41B1B',
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              <strong>Pay rate is below minimum</strong> — the minimum pay rate
              is {formatCurrency(centsToDollars(minPay))}.
            </div>
          ) : null}
          {!isMarkup ? (
            <KeyValueRow>
              <KvLabel>Base bill rate</KvLabel>
              <KvValue>{formatCurrency(centsToDollars(job.cost_rate))}</KvValue>
            </KeyValueRow>
          ) : null}
          <KeyValueRow>
            <KvLabel>Total hours</KvLabel>
            {/* totalShiftHours already weights each shift by its own
                quantity, so multiplying again by job.quantity here
                would double-count when shifts share the job quantity
                and produce 48 instead of 32 in the 8×1 + 8×3 case. */}
            <KvValue>{hours.toFixed(2)}</KvValue>
          </KeyValueRow>
        </RatesSection>
      </Collapsible>
      {editTarget ? (
        <ShiftPopup
          mode="edit"
          orderTypeLabel="gig"
          dateKey={editTarget.dateKey}
          initial={{
            startTime: editTarget.startTime,
            endTime: editTarget.endTime,
            breakTime: editTarget.breakTime,
            workers: editTarget.workers,
          }}
          position={centeredPopupPosition()}
          onSave={handleSaveEdit}
          onClose={() => setEditTarget(null)}
          pastCheck={
            isClientAdmin
              ? (() => {
                  const now = new Date();
                  const pad = (n: number) => String(n).padStart(2, '0');
                  return {
                    todayKey: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
                    nowMinutes: now.getHours() * 60 + now.getMinutes(),
                  };
                })()
              : null
          }
        />
      ) : null}
    </Card>
  );
};

const ReviewStep = ({
  orderType,
  jobs,
  skills,
  paymentMethods,
  paymentMethodsLoading,
  paymentMethodsError,
  billingAccountName,
  customerName,
  billingAccount,
  onUpdateJobs,
  onAddSkill,
  surgeRates = [],
  onRatesValidityChange,
  onPaymentMethodValidityChange,
}: ReviewStepProps) => {
  const { currentAdminIsCustomerAdmin: isClientAdminTop } = useAuth();
  const isMarkup = billingAccount?.rate_type === 'MARKUP';

  // Missing-payment-method state mirrors legacy CreateGigOrder's Billing
  // form (`!accountPaymentMethods.currentPaymentMethod`). We only treat
  // this as "missing" once the fetch has resolved without an error —
  // loading and error states fall through so an in-flight request never
  // hard-blocks the user.
  const paymentMethodMissing =
    !!paymentMethods &&
    !paymentMethodsLoading &&
    !paymentMethodsError &&
    !paymentMethods.current_payment_method;

  useEffect(() => {
    if (!onPaymentMethodValidityChange) return;
    onPaymentMethodValidityChange(!paymentMethodMissing);
  }, [onPaymentMethodValidityChange, paymentMethodMissing]);

  // Deep-link target for the "Click here" CTA. Mirrors the v1 split:
  // client admins manage their own company billing accounts under
  // /my-company, tenant admins manage them under /clients/:customerId.
  // Falls back to null when we lack enough context to build the link, so
  // the warning still renders without a clickable target.
  const accountSettingsPath = (() => {
    if (!billingAccount?.id) return null;
    if (isClientAdminTop) {
      return `/${ROUTES.myCompany}/accounts/${billingAccount.id}`;
    }
    if (billingAccount.customer_id == null) return null;
    return `/${ROUTES.clients}/${billingAccount.customer_id}/accounts/${billingAccount.id}`;
  })();
  const surgeByDate = new Map(surgeRates.map((sr) => [sr.date, sr]));
  // Mirror the legacy SummaryTableRevamped: shifts that fall on a surge
  // date are billed at `cost_rate * surge.rate`. Without this, the review
  // total would understate the estimate for surge-rate customers.
  const surgeAdjustedEstimate = (job: JobDraftV2) => {
    let total = 0;
    for (const shift of job.shifts || []) {
      const start = new Date(shift.start_at).getTime();
      const end = new Date(shift.end_at).getTime();
      let diffMs = end - start;
      if (diffMs <= 0) diffMs += 24 * 3_600_000;
      const hours = Math.max(
        0,
        diffMs / 3_600_000 - (shift.break_minutes || 0) / 60,
      );
      const dateKey = shift.start_at.slice(0, 10);
      const multiplier = surgeByDate.get(dateKey)?.rate ?? 1;
      // Same per-shift-quantity rule as totalShiftHours: a shift with
      // 3 workers costs 3× a 1-worker shift even when the job-level
      // quantity is 1.
      const qty = shift.quantity ?? job.quantity ?? 1;
      total += hours * centsToDollars(job.cost_rate) * multiplier * qty;
    }
    return total;
  };
  const totalHours = jobs.reduce(
    // totalShiftHours is per-shift-quantity weighted — do not multiply
    // again by job.quantity (see the helper docblock above).
    (acc, job) => acc + totalShiftHours(job),
    0,
  );
  const totalEstimate = jobs.reduce(
    (acc, job) => acc + surgeAdjustedEstimate(job),
    0,
  );
  const reviewHolidayDates = jobs.flatMap((job) =>
    (job.shifts || [])
      .map((s) => s.start_at.slice(0, 10))
      .filter((dk) => surgeByDate.has(dk)),
  );
  const uniqueHolidayDates = Array.from(new Set(reviewHolidayDates));

  const headingTarget = customerName ?? billingAccountName ?? null;
  const headingSuffix = headingTarget ? ` for ${headingTarget}` : '';
  const cardLabel = formatCard(paymentMethods);

  const updateJobAt = (index: number, next: JobDraftV2) => {
    if (!onUpdateJobs) return;
    const copy = [...jobs];
    copy[index] = next;
    onUpdateJobs(copy);
  };

  // Per-job validity, aggregated to a single boolean and bubbled up so
  // the wizard footer can disable Submit on any below-minimum pay rate.
  // Missing entries default to `true` (e.g., the rate range hasn't
  // resolved yet) so we never block the user on an in-flight fetch.
  const [perJobValidity, setPerJobValidity] = useState<Record<number, boolean>>(
    {},
  );
  const handleJobValidity = (index: number, valid: boolean) => {
    setPerJobValidity((prev) =>
      prev[index] === valid ? prev : { ...prev, [index]: valid },
    );
  };
  const aggregateValid = jobs.every(
    (_, index) => perJobValidity[index] !== false,
  );
  useEffect(() => {
    if (!onRatesValidityChange) return;
    onRatesValidityChange(aggregateValid);
  }, [onRatesValidityChange, aggregateValid]);

  return (
    <PageShell>
      <OrderHeading>
        Order{headingSuffix}
        {orderType ? ` · ${orderType === 'LTA' ? 'LTA' : 'Gig'}` : ''}
      </OrderHeading>

      <TwoColumn>
        <LeftColumn>
          {jobs.length === 0 ? (
            <Card>
              <JobHeaderRow>
                <JobMeta>
                  <JobName>No jobs configured</JobName>
                  <JobMetaText muted>
                    Go back and pick a skill and schedule to add a job.
                  </JobMetaText>
                </JobMeta>
              </JobHeaderRow>
            </Card>
          ) : (
            jobs.map((job, index) => (
              <JobCard
                key={index}
                job={job}
                index={index}
                skills={skills}
                defaultOpen={index === 0}
                isMarkup={isMarkup}
                markupPercent={billingAccount?.markup_percent ?? 0}
                billingAccountId={billingAccount?.id ?? null}
                onValidityChange={handleJobValidity}
                onUpdateJob={
                  onUpdateJobs ? (next) => updateJobAt(index, next) : undefined
                }
              />
            ))
          )}

          {onAddSkill ? (
            <AddSkillsButton type="button" onClick={onAddSkill}>
              <PlusIcon />
              Add Skills
            </AddSkillsButton>
          ) : null}
        </LeftColumn>

        <RightColumn>
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <KeyValueRow>
              <KvLabel>Total hours</KvLabel>
              <KvValue>{totalHours.toFixed(2)}</KvValue>
            </KeyValueRow>
            <KeyValueRow>
              <KvLabel>Total estimate</KvLabel>
              <KvValue>{formatCurrency(totalEstimate)}</KvValue>
            </KeyValueRow>
            {uniqueHolidayDates.length > 0 ? (
              <div
                style={{
                  marginTop: 12,
                  background: '#FFF6E8',
                  border: '1px solid #FFD49A',
                  color: '#7A4309',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                Estimate includes holiday surge multipliers on{' '}
                {uniqueHolidayDates
                  .map((dk) => {
                    const surge = surgeByDate.get(dk);
                    return surge ? `${dk} (${surge.rate}×)` : dk;
                  })
                  .join(', ')}
                .
              </div>
            ) : null}
            <div style={{ height: 16 }} />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment method</CardTitle>
            </CardHeader>
            <PaymentBody>
              {paymentMethodsLoading ? (
                <PaymentMuted>Loading payment methods…</PaymentMuted>
              ) : paymentMethodsError ? (
                <PaymentMuted style={{ color: '#A0292B' }}>
                  {paymentMethodsError}
                </PaymentMuted>
              ) : cardLabel ? (
                <PaymentLabel>{cardLabel}</PaymentLabel>
              ) : (
                // V1 parity: legacy Billing form renders a danger-colored
                // "There is no payment method associated with this billing
                // account. Click here to add payment method." and disables
                // Start order. The Submit gate is wired in the parent via
                // onPaymentMethodValidityChange so the user can't proceed.
                <div
                  role="alert"
                  style={{
                    padding: '10px 12px',
                    borderRadius: 6,
                    background: '#FDECEC',
                    border: '1px solid #F5B7B7',
                    color: '#A41B1B',
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  <div>
                    <strong>
                      There is no payment method associated with this billing
                      account.
                    </strong>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {accountSettingsPath ? (
                      <Link
                        to={accountSettingsPath}
                        style={{
                          color: '#A41B1B',
                          fontWeight: 700,
                          textDecoration: 'underline',
                        }}
                      >
                        Click here
                      </Link>
                    ) : (
                      <strong>Click here</strong>
                    )}{' '}
                    to add payment method.
                  </div>
                </div>
              )}
            </PaymentBody>
          </Card>
        </RightColumn>
      </TwoColumn>
    </PageShell>
  );
};

export default ReviewStep;
